import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    DialogContent,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Typography,
    Chip
} from "@mui/material/";
import CloseIcon from "@mui/icons-material/Close";
import * as Sentry from "@sentry/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Loading from "../../../../../common/components/Loading";
import { strings } from "../../../../../common/i18n";
import actions from "../../../../../Store/actions";
import styles from "./styles";

const MoveAsset = ({ onClose, selectedAssets }) => {
    const [audit, setAudit] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentAuditGroups, setCurrentAuditGroups] = useState([]);

    const params = useParams();
    const dispatch = useDispatch();

    const { auditsList, auth } = useSelector((state) => state);

    const { auditId } = params;
    // Load audit groups
    useEffect(() => {
        const loadAudits = async () => {
            try {
                setLoading(true);
                if (auditsList?.item === null) {
                    const audit = auditsList.data.find(
                        (a) => a.id === parseInt(auditId)
                    );
                    await dispatch(actions.auditsList.create.itemSet(audit));
                    const { audits } = await dispatch(
                        actions.auditsList.create.getRequest(
                            "active",
                            1,
                            50,
                            "",
                            audit.auditReference
                        )
                    );
                    setCurrentAuditGroups(audits.results || []);
                } else {
                    const { audits } = await dispatch(
                        actions.auditsList.create.getRequest(
                            "active",
                            1,
                            50,
                            "",
                            auditsList.item.auditReference
                        )
                    );
                    setCurrentAuditGroups(audits.results || []);
                }
            } catch (error) {
                console.error("Error loading audits", error);
                Sentry.captureException(error);
            } finally {
                setLoading(false);
            }
        };

        loadAudits();
    }, [auditId]);

    // Set default audit
    useEffect(() => {
        if (auditsList?.item?.id) {
            setAudit(auditsList.item.id);
        }
    }, [auditId, auditsList]);

    const handleAuditChange = (event) => {
        setAudit(event.target.value);
    };

    const handleMoveAsset = async () => {
        try {
            setLoading(true);
            const selectedAudit = currentAuditGroups.find(
                (a) => a.id === audit
            );

            const next = selectedAssets.map((item) => ({
                id: item.id,
                isApproved: item.isApproved,
                isAssigned: item.isAssigned,
                reissuedStatus: item.reissuedStatus,
                updatedBy: item?.updatedBy?.id || auth?.account?.id,
                auditLocation: selectedAudit.auditLocation[0].id
            }));

            await dispatch(actions.auditAssets.create.postRequest(next));
            await dispatch(actions.auditReview.create.getRequest(auditId));
            await dispatch(actions.toast.create.success(strings("assetMoved")));
            onClose?.();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            Sentry.captureException(`Resolve multiple assets: ${errStr}`);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loading loadingText="loadingAudit" />;

    const isSubmitDisabled = audit === auditsList?.item?.id;

    return (
        <>
            <DialogTitle>{strings("moveAsset")}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ p: 3, minHeight: 300, overflowY: "auto" }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {strings("messages.moveAssetConfirmation", {
                        count: selectedAssets.length
                    })}
                </Typography>

                {/* Selected Assets as Chips */}
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        bgcolor: "#fafafa",
                        minHeight: 100,
                        maxHeight: 180,
                        overflowY: "auto"
                    }}
                >
                    <FormControl fullWidth>
                        <InputLabel>{strings("audit")}</InputLabel>
                        <Select
                            value={audit || ""}
                            onChange={(e) => setAudit(e.target.value)}
                        >
                            {currentAuditGroups.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Audit Selector */}
            </DialogContent>

            <DialogActions sx={{ m: 2 }}>
                <Button
                    variant="contained"
                    sx={styles.primaryButton}
                    onClick={handleMoveAsset}
                    disabled={isSubmitDisabled}
                >
                    {strings("move")}
                </Button>
            </DialogActions>
        </>
    );
};

export default MoveAsset;
